import axios from "axios";
import { BACKEND_URL } from "../../../utils/api";
import { WalletHistory, WalletStats, Wallet } from "../types/Wallet";

export const walletService = {
  async searchWallets(
    token: string,
    searchQuery: string,
    page: number = 1,
    pageSize: number = 10,
    sortBy: string = "name",
    sortOrder: string = "asc"
  ): Promise<{
    wallets: Wallet[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const params = new URLSearchParams({
      q: searchQuery,
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    const response = await axios.get(`${BACKEND_URL}/wallet/search?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },

  async getWalletStats(token: string): Promise<WalletStats> {
    const response = await axios.get(`${BACKEND_URL}/wallet/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },

  async getWallets(
    token: string,
    page: number = 1,
    pageSize: number = 10,
    userId?: string,
    sortBy: string = "name",
    sortOrder: string = "asc",
    negativeOnly: boolean = false
  ): Promise<{
    wallets: Wallet[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    if (userId) params.append("user_id", userId);
    if (negativeOnly) params.append("negative_only", "true");
    const response = await axios.get(`${BACKEND_URL}/wallet/?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },

  async getWalletsByUser(
    token: string,
    userId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    wallets: Wallet[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const response = await axios.get(`${BACKEND_URL}/wallet/user/${userId}?page=${page}&page_size=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },

  async rechargeWallet(
    token: string,
    data: { user_id: string; country_id: number; amount: number; description?: string }
  ): Promise<Wallet> {
    const response = await axios.post(`${BACKEND_URL}/wallet/recharge`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.wallet;
  },

  async adminRechargeWallet(
    token: string,
    data: { user_id: string; country_id: number; amount: number; description?: string }
  ): Promise<any> {
    const response = await axios.post(`${BACKEND_URL}/wallet/admin/recharge`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async debitWallet(
    token: string,
    data: { user_id: string; country_id: number; amount: number; description?: string }
  ): Promise<Wallet> {
    const response = await axios.post(`${BACKEND_URL}/wallet/admin/debit`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.wallet;
  },

  async updateDailyLimit(token: string, walletId: string, dailyLimit: number): Promise<Wallet> {
    const response = await axios.put(
      `${BACKEND_URL}/wallet/${walletId}/daily-limit`,
      { daily_limit: dailyLimit },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.data.wallet;
  },

  async updateStatus(
    token: string,
    data: { id: string; wallet_id: string; status: WalletHistory["status"] }
  ): Promise<Wallet> {
    const response = await axios.post(`${BACKEND_URL}/wallet/history/update-status`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.wallet;
  },
  async searchWalletHistories(
    token: string,
    searchQuery: string,
    page: number = 1,
    pageSize: number = 50,
    type?: string,
    sortBy: string = "created_at",
    sortOrder: string = "desc",
    startDate?: string,
    endDate?: string,
    status?: string
  ): Promise<{
    histories: WalletHistory[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const params = new URLSearchParams({
      q: searchQuery,
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    if (type) params.append("type", type);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (status) params.append("status", status);
    const response = await axios.get(`${BACKEND_URL}/wallet/history/search?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },

  async getWalletHistories(
    token: string,
    page: number = 1,
    pageSize: number = 50,
    userId?: string,
    walletId?: number,
    type?: string,
    sortBy: string = "created_at",
    sortOrder: string = "desc",
    startDate?: string,
    endDate?: string,
    status?: string
  ): Promise<{
    histories: WalletHistory[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    if (userId) params.append("user_id", userId);
    if (walletId) params.append("wallet_id", String(walletId));
    if (type) params.append("type", type);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (status) params.append("status", status);
    const response = await axios.get(`${BACKEND_URL}/wallet/history/all?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },
  async getWalletHistory(
    token: string,
    walletId: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    histories: WalletHistory[];
    pagination: { page: number; page_size: number; total: number; total_pages: number };
  }> {
    const response = await axios.get(`${BACKEND_URL}/wallet/history/${walletId}?page=${page}&page_size=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },
};
